import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../utils/errors';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import SearchInput from '../../components/SearchInput/SearchInput';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import {
  useListAddresses,
  useListCustomers,
  useSetCustomerLocationProductAccess,
} from '../../features/customers/api';

// Form rules — pure, unit-tested, no React. See features/products/productForm.
import {
  buildCreatePayload,
  buildDetailsPayload,
  buildErrorList,
  buildFormValuesFromProduct,
  buildSnapshotFromProduct,
  canonicalizeForm,
  computeDirty,
  emptySnapshot,
  selectEnabledWarehouses,
  snapshotAfterSave,
  validateProductForm,
} from '../../features/products/productForm';
import type {
  PalletRow,
  ProductSnapshot,
  WarehouseState,
} from '../../features/products/productForm';

// TEMPLATES & WAREHOUSES
import { useListAvailableLocations } from '../../features/warehouses/api';
import { useListPalletSizes } from '../../features/loads/api';

// CREATE
import { useCreateProduct } from '../../features/products/api';

// EDIT fetch & mutations
import {
  useGetProduct,
  useUpdateProductDetails,
  useReplaceStocks,
  useReplacePalletSizes,
} from '../../features/products/detail.api';
import './products.scss';
import fallbackProductImage from '../../assets/defeo-logo.png';

const DTYPES = ['BULK', 'PALLET'];

type CustomerProductAccessState = 'ENABLED' | 'PREVIEW' | 'DISABLED';

type CustomerLocationProductChange = {
  deliveryAddressId: string;
  isEnabled: boolean;
  isPreview: boolean;
};

type CustomerLocationAccessChanges = Record<string, CustomerProductAccessState>;

function customerDisplayLabel(customer: any): string {
  if (!customer) {
    return '';
  }

  return String(customer.name || customer.email || customer.id || 'Customer');
}

function deliveryAddressDisplayLabel(address: any): string {
  if (!address) {
    return '';
  }

  const heading = address.label || address.fullName || 'Delivery location';
  const location = [address.line1, address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(', ');

  return location ? `${heading} — ${location}` : String(heading);
}

function locationAccessFromAddress(address: any, productId?: string): CustomerProductAccessState {
  if (!productId) {
    return 'DISABLED';
  }

  const assignments = Array.isArray(address?.products) ? address.products : [];
  const assignment = assignments.find((row: any) => row?.productId === productId);

  if (assignment?.isEnabled === true) {
    return 'ENABLED';
  }

  if (assignment?.isPreview === true) {
    return 'PREVIEW';
  }

  return 'DISABLED';
}

function buildCustomerLocationProductChanges(
  changes: CustomerLocationAccessChanges
): CustomerLocationProductChange[] {
  return Object.entries(changes).map(([deliveryAddressId, access]) => ({
    deliveryAddressId,
    isEnabled: access === 'ENABLED',
    isPreview: access === 'PREVIEW',
  }));
}

/* ----------------- component ----------------- */
export default function ProductCreatePage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();

  // Templates + Warehouses
  const { data: warehouses = [], isLoading: whLoading } = useListAvailableLocations();
  // const { data: loadTemplates = [] } = useListLoadOptions();
  const { data: palletTemplates = [] } = useListPalletSizes();

  // Create or Edit hooks
  const createProduct = useCreateProduct();
  const { data: product, isLoading: productLoading } = useGetProduct(id || '', { enabled: isEdit });
  const updateDetails = useUpdateProductDetails(id || '');
  const replaceStocks = useReplaceStocks(id || '');
  const replacePallets = useReplacePalletSizes(id || '');
  const setCustomerLocationProductAccess = useSetCustomerLocationProductAccess();
  // const replaceLoads = useReplaceLoadOptions(id || "");
  // const { data: orgData } = useGetOrganization();

  // ---------- STATE ----------
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [description, setDesc] = useState('');
  const [deliveryType, setDeliveryType] = useState('BULK');
  const [trackInventory, setTrackInventory] = useState(false);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  // The saved photo was cleared; on save we tell the server to blank imageUrl.
  const [removeImage, setRemoveImage] = useState(false);
  const displayedImage = imagePreview || fallbackProductImage;

  // { [locationId]: { enabled: boolean, quantity: string } }
  const [whState, setWhState] = useState<WarehouseState>({});
  const [defaultLocationId, setDefaultLocationId] = useState('');
  const enabledWarehouses = useMemo(
    () => selectEnabledWarehouses(warehouses as Array<{ id: string }>, whState),
    [warehouses, whState]
  );

  const warehouseName = (w: any) =>
    w?.name || [w?.line1, w?.city, w?.state].filter(Boolean).join(', ') || 'Warehouse';

  // Options for the add-list: only warehouses not already added, so picking one
  // removes it from the dropdown.
  const warehousesToAdd = useMemo(
    () =>
      (warehouses as any[])
        .filter((w) => !whState[w.id]?.enabled)
        .map((w) => ({ id: w.id, name: warehouseName(w) })),
    [warehouses, whState]
  );

  const addWarehouse = (locId: string) =>
    setWhState((s) => {
      const prev = s[locId] ?? { quantity: '0', stonePrice: '', enabled: false };
      return { ...s, [locId]: { ...prev, enabled: true } };
    });

  const removeWarehouse = (locId: string) => {
    setWhState((s) => {
      const prev = s[locId] ?? { quantity: '0', stonePrice: '', enabled: false };
      return { ...s, [locId]: { ...prev, enabled: false } };
    });
    if (defaultLocationId === locId) setDefaultLocationId('');
  };

  // A lone sell-from location is always the default — on add and going forward.
  useEffect(() => {
    if (enabledWarehouses.length === 1 && defaultLocationId !== enabledWarehouses[0].id) {
      setDefaultLocationId(enabledWarehouses[0].id);
    }
  }, [enabledWarehouses, defaultLocationId]);

  const [palletRows, setPalletRows] = useState<PalletRow[]>([]);

  // ---------- CUSTOMER / DELIVERY-LOCATION PRODUCT ACCESS ----------
  //
  // Product access is location-specific. The customer picker is only a
  // convenient way to choose which customer's DeliveryAddresses to edit.
  // The persisted rows are always CustomerLocationProduct rows.
  const [accessCustomerSearch, setAccessCustomerSearch] = useState('');
  const debouncedAccessCustomerSearch = useDebouncedSearch(accessCustomerSearch, 350);
  const [accessCustomerId, setAccessCustomerId] = useState('');
  const [accessCustomerLabel, setAccessCustomerLabel] = useState('');
  const [locationAccessChanges, setLocationAccessChanges] = useState<CustomerLocationAccessChanges>(
    {}
  );

  const accessCustomersQuery = useListCustomers({
    page: 1,
    pageSize: 50,
    search: debouncedAccessCustomerSearch,
  });

  const accessCustomers = useMemo(() => {
    return Array.isArray(accessCustomersQuery.data?.customers)
      ? accessCustomersQuery.data.customers
      : [];
  }, [accessCustomersQuery.data]);

  const {
    data: accessAddressesData,
    isLoading: accessAddressesLoading,
    isFetching: accessAddressesFetching,
    refetch: refetchAccessAddresses,
  } = useListAddresses(accessCustomerId || null);

  const accessAddresses = useMemo(() => {
    return Array.isArray(accessAddressesData?.addresses) ? accessAddressesData.addresses : [];
  }, [accessAddressesData]);

  const perItemPricingForAccessCustomer = accessAddressesData?.perItemPricing;

  const getBaseLocationAccess = useCallback(
    (address: any): CustomerProductAccessState => {
      return locationAccessFromAddress(address, isEdit ? id : undefined);
    },
    [id, isEdit]
  );

  const getEffectiveLocationAccess = useCallback(
    (address: any): CustomerProductAccessState => {
      return locationAccessChanges[address.id] ?? getBaseLocationAccess(address);
    },
    [getBaseLocationAccess, locationAccessChanges]
  );

  const setLocationAccess = useCallback(
    (address: any, access: CustomerProductAccessState) => {
      if (!address?.id) {
        return;
      }

      const baseAccess = getBaseLocationAccess(address);

      setLocationAccessChanges((current) => {
        const next = { ...current };

        // If the admin puts this location back to the persisted value,
        // remove the override entirely. This keeps accessDirty accurate.
        if (access === baseAccess) {
          delete next[address.id];
        } else {
          next[address.id] = access;
        }

        return next;
      });
    },
    [getBaseLocationAccess]
  );

  const setAllSelectedCustomerLocations = useCallback(
    (access: CustomerProductAccessState) => {
      for (const address of accessAddresses) {
        setLocationAccess(address, access);
      }
    },
    [accessAddresses, setLocationAccess]
  );

  const selectedCustomerAccessCounts = useMemo(() => {
    const counts = {
      enabled: 0,
      preview: 0,
      disabled: 0,
    };

    for (const address of accessAddresses) {
      const access = getEffectiveLocationAccess(address);

      if (access === 'ENABLED') {
        counts.enabled += 1;
      } else if (access === 'PREVIEW') {
        counts.preview += 1;
      } else {
        counts.disabled += 1;
      }
    }

    return counts;
  }, [accessAddresses, getEffectiveLocationAccess]);

  const customerLocationProductChanges = useMemo(
    () => buildCustomerLocationProductChanges(locationAccessChanges),
    [locationAccessChanges]
  );

  const accessDirty = customerLocationProductChanges.length > 0;

  const fileRef = useRef<HTMLInputElement>(null);

  /* ---- snapshot of initial server state for dirty checks ---- */
  /** Canonical snapshot of the saved product, compared against the form. */
  const initialRef = useRef<ProductSnapshot>(emptySnapshot());

  const resetForm = useCallback(() => {
    setName('');
    setSku('');
    setDesc('');
    setDeliveryType('BULK');
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(false);
    setWhState({});
    setDefaultLocationId('');
    setPalletRows([]);
    // setLoadRows([]);
    setTrackInventory(false);
    setAccessCustomerSearch('');
    setAccessCustomerId('');
    setAccessCustomerLabel('');
    setLocationAccessChanges({});
    initialRef.current = emptySnapshot();
  }, []);

  useEffect(() => {
    if (!isEdit) resetForm();
  }, [isEdit, resetForm]);

  useEffect(() => {
    if (!isEdit) return;

    setAccessCustomerSearch('');
    setAccessCustomerId('');
    setAccessCustomerLabel('');
    setLocationAccessChanges({});
  }, [id, isEdit]);

  // ---------- HYDRATE WHEN EDIT ----------
  useEffect(() => {
    if (!isEdit) return;
    if (productLoading || !product) return;

    const values = buildFormValuesFromProduct(product, warehouses as Array<{ id: string }>);

    setName(values.name);
    setSku(values.sku);
    setDesc(values.description);
    setDeliveryType(values.deliveryType);
    setImagePreview(values.imagePreview);
    setTrackInventory(values.trackInventory);
    setImageFile(values.imageFile);
    setRemoveImage(false);
    setWhState(values.whState);
    setDefaultLocationId(values.defaultLocationId);
    setPalletRows(values.palletRows);

    // build initial snapshot for dirty checks (canonical)
    initialRef.current = buildSnapshotFromProduct(product);
  }, [isEdit, product, productLoading, warehouses]);

  // ---------- IMAGE PICK ----------
  const onPickImage = (file: File | null | undefined) => {
    if (!file) {
      setImageFile(null);
      setImagePreview(product?.imageUrl ?? '');
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(String(e.target?.result ?? ''));
    reader.readAsDataURL(file);
  };

  // The delete mark: a freshly-picked file is just discarded (reverts to the
  // saved photo); the saved photo is flagged for removal so the next save blanks it.
  const onClearImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (imageFile) {
      setImageFile(null);
      setImagePreview(product?.imageUrl ?? '');
      setRemoveImage(false);
    } else {
      setImagePreview('');
      setRemoveImage(true);
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) onPickImage(e.dataTransfer.files[0]);
  };

  // ---------- PALLET helpers ----------
  const addPalletFromTemplate = (tpl: any) =>
    setPalletRows((rows) => [
      ...rows,
      {
        id: tpl.id,
        name: tpl.name ?? '',
        type: tpl.type ?? '',
        lengthIn: tpl.lengthIn ?? '',
        widthIn: tpl.widthIn ?? '',
        heightIn: tpl.heightIn ?? '',
        weightTons: tpl.weightTons ?? '',
      },
    ]);
  const removePallet = (idx: number) => setPalletRows((rows) => rows.filter((_, i) => i !== idx));

  // ---------- VALIDATION ----------
  const validation = useMemo(
    () =>
      validateProductForm({
        name,
        imagePreview: displayedImage,
        deliveryType,
        defaultLocationId,
        enabledWarehouses,
        palletRows,
      }),
    [name, displayedImage, enabledWarehouses, palletRows, deliveryType, defaultLocationId]
  );

  const errorList = useMemo(
    () => buildErrorList(validation.errors, deliveryType),
    [validation, deliveryType]
  );

  const scrollToSection = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  // ---------- SUBMIT ----------
  const onSubmit = async () => {
    if (!validation.isValid) {
      toast.error('Please fix the highlighted fields.');
      if (errorList[0]) scrollToSection(errorList[0].id);
      return;
    }

    // build current canonical forms
    const current = canonicalizeForm({
      name,
      sku,
      description,
      deliveryType,
      trackInventory,
      defaultLocationId,
      whState,
      palletRows,
      imageFile,
      imagePreview,
    });

    if (!isEdit) {
      try {
        /*
         * Keep the existing typed product payload intact. Product creation
         * already owns its multipart/FormData conversion inside the products
         * feature API. Customer-location access is a separate customer-domain
         * mutation, so this component never casts the product payload to
         * FormData.
         */
        const created = await createProduct.mutateAsync(buildCreatePayload(current, imageFile));

        const createdProductId = String(
          (created as any)?.id ?? (created as any)?.product?.id ?? ''
        ).trim();

        if (customerLocationProductChanges.length > 0) {
          if (!createdProductId) {
            throw new Error('CREATED_PRODUCT_ID_MISSING');
          }

          await Promise.all(
            customerLocationProductChanges.map((change) =>
              setCustomerLocationProductAccess.mutateAsync({
                addressId: change.deliveryAddressId,
                productId: createdProductId,
                isEnabled: change.isEnabled,
                isPreview: change.isPreview,
              })
            )
          );
        }

        toast.success('Product created.');
        nav(listUrl());
      } catch (e) {
        console.error(e);
        toast.error(apiErrorMessage(e, 'Failed to create product.'));
      }
      return;
    }

    // EDIT path — compare with snapshot and call only what changed
    const dirty = computeDirty(current, initialRef.current, Boolean(imageFile), removeImage);

    if (!dirty.anyDirty && !accessDirty) {
      toast.info('No changes to save.');
      return;
    }

    try {
      if (dirty.detailsDirty) {
        await updateDetails.mutateAsync(
          buildDetailsPayload(current.details, imageFile, removeImage)
        );
      }
      if (dirty.stocksDirty) {
        await replaceStocks.mutateAsync(current.stocks);
      }
      if (dirty.palletsDirty) {
        await replacePallets.mutateAsync(current.pallets as any);
      }

      if (accessDirty) {
        if (!id) {
          throw new Error('PRODUCT_ID_REQUIRED_FOR_ACCESS_UPDATE');
        }

        await Promise.all(
          customerLocationProductChanges.map((change) =>
            setCustomerLocationProductAccess.mutateAsync({
              addressId: change.deliveryAddressId,

              productId: id,

              isEnabled: change.isEnabled,

              isPreview: change.isPreview,
            })
          )
        );

        await refetchAccessAddresses();
      }

      toast.success('Product updated.');

      // refresh snapshot after successful save
      initialRef.current = snapshotAfterSave(current);
      setLocationAccessChanges({});

      nav(listUrl());
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessage(e, 'Failed to update product.'));
    }
  };

  /* ----------------- UI below (unchanged) ----------------- */
  const headerTitle = isEdit ? 'Edit Product' : 'Create Product';
  const primaryCta = isEdit ? 'Save Changes' : 'Create Product';

  // Saving returns to the products list with its last search/filters preserved.
  const listUrl = () => window.localStorage.getItem('products-last-url') || '/products';

  return (
    <div className="products-create">
      {/* Error Summary Banner */}
      {errorList.length > 0 && (
        <div
          className="card"
          style={{
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#7f1d1d',
            marginBottom: 12,
          }}
          role="alert"
          aria-live="polite"
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Please fix the following:</div>
          <ul style={{ paddingLeft: 18, margin: 0, display: 'grid', gap: 4 }}>
            {errorList.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(e.scrollTo || e.id)}
                  style={{
                    background: 'transparent',
                    border: 0,
                    textDecoration: 'underline',
                    color: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {e.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="products-create__grid products-create__grid--single">
        <div className="products-create__main">
          <div className="card">
            <div className="card__title-row">
              <h2 className="card__title">{headerTitle}</h2>
              {isEdit && <div className="pill">Full editor</div>}
            </div>
            <p className="card__subtitle">
              Define where you sell, how it ships, and per-warehouse load prices.
            </p>

            {/* Details */}
            <section
              id="section-details"
              className={`section ${validation.errors.name ? 'has-error has-error--name-only' : ''}`}
            >
              <div className="section__header">
                <div className="section__step">1</div>
                <div>
                  <h3 className="section__title">Details</h3>
                  <p className="section__hint">Name, SKU, delivery type, description and image.</p>
                </div>
              </div>

              <div className="grid grid--2">
                <div
                  className={`form-control ${validation.errors.name ? 'form-control--error' : ''}`}
                >
                  <label>Name *</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Road Salt"
                    aria-invalid={validation.errors.name || undefined}
                  />
                  {validation.errors.name && (
                    <p className="field-error">Product name is required.</p>
                  )}
                </div>

                <div className="form-control">
                  <label>SKU</label>
                  <input
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder="BBL-13-NES"
                    autoComplete="off"
                  />
                  <small className="muted">
                    Optional. Used when creating this product in QuickBooks.
                  </small>
                </div>

                <div className="form-control">
                  <label>Delivery Type *</label>
                  <div className="segmented">
                    {DTYPES.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={`segmented__btn ${deliveryType === d ? 'is-active' : ''}`}
                        onClick={() => setDeliveryType(d)}
                      >
                        {d === 'BULK' ? 'Bulk' : 'Pallet'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-control">
                  <label>Description</label>
                  <input
                    value={description}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="Short description…"
                  />
                </div>

                {/* Dropzone */}
                <div className="form-control" style={{ gridColumn: '1 / -1' }}>
                  <label>Image</label>

                  <div
                    className="dropzone"
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => onPickImage(e.target.files?.[0])}
                      style={{ display: 'none' }}
                    />

                    <div className="dropzone__media">
                      <img
                        className="dropzone__preview"
                        src={displayedImage}
                        alt="Product preview"
                      />
                      {imagePreview && (
                        <button
                          className="dropzone__delete"
                          type="button"
                          aria-label="Remove photo"
                          title="Remove photo"
                          onClick={onClearImage}
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <span className="dropzone__hint">
                      {imagePreview ? 'Click to replace' : 'Using default image · click to upload'}
                    </span>
                  </div>

                  <small className="muted">
                    Optional. If no image is uploaded, the default product image will be used.
                    Square-ish images look best.
                  </small>
                </div>
              </div>
            </section>

            {/* Warehouses */}
            <section
              id="section-warehouses"
              className={`section ${validation.errors.warehouses ? 'has-error has-error--full' : ''}`}
            >
              <div className="section__header">
                <div className="section__step">2</div>
                <div>
                  <h3 className="section__title">Warehouses & Inventory</h3>
                  <p className="section__hint">Enable locations and set initial quantity.</p>
                </div>
              </div>

              {whLoading ? (
                <div className="skeleton" style={{ height: 120 }} />
              ) : (
                <>
                  {/* Searchable add-list; picking a warehouse drops it from the options. */}
                  <div className="form-control" style={{ maxWidth: 420, marginBottom: 12 }}>
                    <label>Add a warehouse</label>
                    <SearchableSelect
                      value=""
                      options={warehousesToAdd}
                      onChange={addWarehouse}
                      placeholder={
                        (warehouses as any[]).length === 0
                          ? 'No warehouses configured'
                          : warehousesToAdd.length
                            ? 'Search warehouses…'
                            : 'All warehouses added'
                      }
                      disabled={warehousesToAdd.length === 0}
                      ariaLabel="Add a warehouse"
                    />
                  </div>

                  {enabledWarehouses.length === 0 ? (
                    <div className="muted">
                      {(warehouses as any[]).length === 0
                        ? 'No warehouses. Add some under Organization → Warehouses.'
                        : 'No warehouses added yet. Use the search above to add where this product sells.'}
                    </div>
                  ) : (
                    <div className="table-scroll">
                      <table className="table product-warehouses">
                        <thead>
                          <tr>
                            <th className="wh-default">Default</th>
                            <th>Warehouse</th>
                            {trackInventory && <th>Initial Quantity</th>}
                            <th>
                              Stone price ($/ton) — Southern pickup
                              <div className="muted tiny">
                                Same for every customer; varies by warehouse
                              </div>
                            </th>
                            <th className="col-actions ta-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enabledWarehouses.map((w: any) => {
                            const state = whState[w.id] || {
                              enabled: true,
                              quantity: '0',
                              stonePrice: '',
                            };
                            return (
                              <tr key={w.id}>
                                <td className="wh-default">
                                  <input
                                    type="radio"
                                    name="defaultLocationId"
                                    aria-label={`Set ${warehouseName(w)} as default`}
                                    checked={defaultLocationId === w.id}
                                    onChange={() => setDefaultLocationId(w.id)}
                                  />
                                </td>
                                <td className="wh-name">{warehouseName(w)}</td>
                                {trackInventory && (
                                  <td style={{ width: 160 }}>
                                    <input
                                      className="input--sm"
                                      type="number"
                                      min={0}
                                      value={state.quantity}
                                      onChange={(e) =>
                                        setWhState((s) => ({
                                          ...s,
                                          [w.id]: { ...state, quantity: e.target.value },
                                        }))
                                      }
                                    />
                                  </td>
                                )}
                                <td style={{ width: 160 }}>
                                  <input
                                    className="input--sm"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    placeholder="—"
                                    value={state.stonePrice}
                                    onChange={(e) =>
                                      setWhState((s) => ({
                                        ...s,
                                        [w.id]: { ...state, stonePrice: e.target.value },
                                      }))
                                    }
                                  />
                                </td>
                                <td className="col-actions actions-cell ta-right">
                                  <button
                                    className="btn btn--ghost"
                                    type="button"
                                    onClick={() => removeWarehouse(w.id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {validation.errors.warehouses && (
                <p className="field-error" style={{ marginTop: 8 }}>
                  Select at least one warehouse and set initial quantity.
                </p>
              )}
              {validation.errors.defaultLocation && (
                <p className="field-error" style={{ marginTop: 8 }}>
                  Select a default warehouse.
                </p>
              )}
            </section>

            {/* Customer / Delivery Location Access */}
            <section id="section-customer-access" className="section">
              <div className="section__header">
                <div className="section__step">3</div>
                <div>
                  <h3 className="section__title">Customer & Delivery Location Access</h3>
                  <p className="section__hint">
                    Choose a customer, then set this product to Enabled, Preview, or Disabled for
                    each of that customer's delivery locations.
                  </p>
                </div>
              </div>

              <div className="grid grid--2" style={{ alignItems: 'end', marginBottom: 12 }}>
                <div className="form-control">
                  <label>Search customers</label>
                  <SearchInput
                    value={accessCustomerSearch}
                    onValueChange={setAccessCustomerSearch}
                    placeholder="Name or email…"
                  />
                </div>

                <div className="form-control">
                  <label>Customer</label>
                  <select
                    value={accessCustomerId}
                    onChange={(event) => {
                      const nextId = event.target.value;
                      const customer = accessCustomers.find((row: any) => row.id === nextId);

                      setAccessCustomerId(nextId);
                      setAccessCustomerLabel(customerDisplayLabel(customer) || nextId);
                    }}
                  >
                    <option value="">
                      {accessCustomersQuery.isLoading ? 'Loading customers…' : 'Select customer…'}
                    </option>

                    {accessCustomerId &&
                      !accessCustomers.some(
                        (customer: any) => customer.id === accessCustomerId
                      ) && (
                        <option value={accessCustomerId}>
                          {accessCustomerLabel || accessCustomerId}
                        </option>
                      )}

                    {accessCustomers.map((customer: any) => (
                      <option key={customer.id} value={customer.id}>
                        {customerDisplayLabel(customer)}
                        {customer.email && customer.name ? ` • ${customer.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {accessDirty && (
                <div
                  className="muted tiny"
                  style={{
                    marginBottom: 12,
                    padding: '8px 10px',
                    border: '1px solid #dbeafe',
                    background: '#eff6ff',
                    borderRadius: 10,
                  }}
                >
                  {customerLocationProductChanges.length} unsaved customer-location access{' '}
                  {customerLocationProductChanges.length === 1 ? 'change' : 'changes'}. Changes from
                  customers you previously edited on this page are preserved until you save. Product
                  details use the products API; location access is then persisted through the
                  customer-location access API.
                </div>
              )}

              {!accessCustomerId ? (
                <div className="muted">
                  Select a customer to configure where this product is enabled or available for
                  preview.
                </div>
              ) : accessAddressesLoading ? (
                <div className="skeleton" style={{ height: 140 }} />
              ) : perItemPricingForAccessCustomer === false ? (
                <div
                  className="muted"
                  style={{
                    padding: 12,
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    background: '#f8fafc',
                  }}
                >
                  Per-item pricing is disabled for this organization. All active products are
                  available without explicit CustomerLocationProduct rows, so customer-location
                  access is read-only here. Existing explicit rows are left untouched so they remain
                  available if per-item pricing is enabled again later.
                </div>
              ) : accessAddresses.length === 0 ? (
                <div className="muted">This customer has no delivery locations.</div>
              ) : (
                <>
                  <div
                    className="flex-between"
                    style={{ alignItems: 'center', gap: 12, marginBottom: 10 }}
                  >
                    <div className="muted tiny">
                      <strong>{accessCustomerLabel || 'Selected customer'}</strong> ·{' '}
                      {selectedCustomerAccessCounts.enabled} enabled ·{' '}
                      {selectedCustomerAccessCounts.preview} preview ·{' '}
                      {selectedCustomerAccessCounts.disabled} disabled
                      {accessAddressesFetching ? ' · Refreshing…' : ''}
                    </div>

                    <div className="actions" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setAllSelectedCustomerLocations('ENABLED')}
                      >
                        Enable all locations
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setAllSelectedCustomerLocations('PREVIEW')}
                      >
                        Preview all
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setAllSelectedCustomerLocations('DISABLED')}
                      >
                        Disable all
                      </button>
                    </div>
                  </div>

                  <div className="table-scroll">
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: 180 }}>Access</th>
                          <th>Delivery Location</th>
                          <th style={{ width: 180 }}>Location Type</th>
                        </tr>
                      </thead>

                      <tbody>
                        {accessAddresses.map((address: any) => {
                          const access = getEffectiveLocationAccess(address);
                          const isChanged = locationAccessChanges[address.id] !== undefined;

                          return (
                            <tr key={address.id}>
                              <td>
                                <select
                                  value={access}
                                  onChange={(event) =>
                                    setLocationAccess(
                                      address,
                                      event.target.value as CustomerProductAccessState
                                    )
                                  }
                                >
                                  <option value="ENABLED">Enabled</option>
                                  <option value="PREVIEW">Preview</option>
                                  <option value="DISABLED">Disabled</option>
                                </select>

                                {isChanged && (
                                  <div className="muted tiny" style={{ marginTop: 4 }}>
                                    Unsaved
                                  </div>
                                )}
                              </td>

                              <td>
                                <div style={{ fontWeight: 600 }}>
                                  {deliveryAddressDisplayLabel(address)}
                                </div>
                                {address.phone && (
                                  <div className="muted tiny" style={{ marginTop: 3 }}>
                                    {address.phone}
                                  </div>
                                )}
                              </td>

                              <td>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {address.isPrimary && <span className="pill">Primary</span>}
                                  {address.isHeadquarters && (
                                    <span className="pill">Headquarters</span>
                                  )}
                                  {!address.isPrimary && !address.isHeadquarters && (
                                    <span className="muted tiny">Delivery location</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="muted tiny" style={{ marginTop: 8 }}>
                    Enabled = customer can order this product at that location. Preview = customer
                    can see/request it but cannot order it there. Disabled = no
                    CustomerLocationProduct row is stored.
                  </div>
                </>
              )}
            </section>

            {/* Pallet sizes */}
            {deliveryType === 'PALLET' && (
              <section
                id="section-pallets"
                className={`section ${validation.errors.pallets ? 'has-error has-error--full' : ''}`}
              >
                <div className="section__header">
                  <div className="section__step">4</div>
                  <div>
                    <h3 className="section__title">Pallet Sizes</h3>
                    <p className="section__hint">Add from template or define custom sizes.</p>
                  </div>
                  <div className="section__actions">
                    <select
                      onChange={(e) => {
                        const t = palletTemplates.find((x: any) => x.id === e.target.value);
                        if (t) addPalletFromTemplate(t);
                        e.target.value = '';
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Add from template…
                      </option>
                      {palletTemplates.map((t: any) => (
                        <option key={t.id} value={t.id}>
                          {t.name || `${t.lengthIn}x${t.widthIn}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Length (in)</th>
                        <th>Width (in)</th>
                        <th>Height (in)</th>
                        <th>Weight (tons)</th>
                        <th className="col-actions ta-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {palletRows.map((r, idx) => (
                        <tr key={r.id || `new-${idx}`}>
                          <td>{r.name}</td>
                          <td>{r.type}</td>
                          <td>{r.lengthIn}</td>
                          <td>{r.widthIn}</td>
                          <td>{r.heightIn}</td>
                          <td>{r.weightTons}</td>
                          <td className="col-actions actions-cell ta-right">
                            <button
                              className="btn btn--ghost"
                              type="button"
                              onClick={() => removePallet(idx)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                      {palletRows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="muted ta-center">
                            No pallet sizes yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {validation.errors.pallets && (
                  <p className="field-error" style={{ marginTop: 8 }}>
                    Add at least one pallet size with length, width, and height.
                  </p>
                )}
              </section>
            )}

            <div className="actions" style={{ marginTop: 16 }}>
              <button
                className="btn btn--primary"
                onClick={onSubmit}
                disabled={
                  (isEdit
                    ? updateDetails.isPending ||
                      replaceStocks.isPending ||
                      replacePallets.isPending ||
                      setCustomerLocationProductAccess.isPending
                    : createProduct.isPending || setCustomerLocationProductAccess.isPending) ||
                  !validation.isValid
                }
              >
                {isEdit
                  ? updateDetails.isPending ||
                    replaceStocks.isPending ||
                    replacePallets.isPending ||
                    setCustomerLocationProductAccess.isPending
                    ? 'Saving…'
                    : primaryCta
                  : createProduct.isPending || setCustomerLocationProductAccess.isPending
                    ? 'Creating…'
                    : primaryCta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
